import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import { getAdminStats, getAdminPayments } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, BookOpen, CreditCard, DollarSign, TrendingUp, GraduationCap } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, paymentsData] = await Promise.all([getAdminStats(), getAdminPayments()]);
        setStats(statsData);
        setRecentPayments(paymentsData.slice(0, 5));
      } catch (error) { console.error('Error:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">Panel de Administracion</h1>
          <p className="text-[#737373] mt-2">Bienvenido, {user?.name}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#737373]">Usuarios</p><p className="text-2xl font-bold text-[#1A1A1A]">{stats?.total_users || 0}</p></div><div className="h-10 w-10 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><Users className="h-5 w-5 text-[#8B1538]" /></div></div></CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#737373]">Estudiantes</p><p className="text-2xl font-bold text-[#1A1A1A]">{stats?.total_students || 0}</p></div><div className="h-10 w-10 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center"><GraduationCap className="h-5 w-5 text-[#2D6A4F]" /></div></div></CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#737373]">Profesores</p><p className="text-2xl font-bold text-[#1A1A1A]">{stats?.total_teachers || 0}</p></div><div className="h-10 w-10 rounded-full bg-[#D9B48F]/30 flex items-center justify-center"><Users className="h-5 w-5 text-[#B8860B]" /></div></div></CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#737373]">Modulos</p><p className="text-2xl font-bold text-[#1A1A1A]">{stats?.total_courses || 0}</p></div><div className="h-10 w-10 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><BookOpen className="h-5 w-5 text-[#8B1538]" /></div></div></CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#737373]">Inscripciones</p><p className="text-2xl font-bold text-[#1A1A1A]">{stats?.total_enrollments || 0}</p></div><div className="h-10 w-10 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-[#2D6A4F]" /></div></div></CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#737373]">Ingresos</p><p className="text-2xl font-bold text-[#1A1A1A]">${(stats?.total_revenue || 0).toLocaleString()}</p></div><div className="h-10 w-10 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><DollarSign className="h-5 w-5 text-[#8B1538]" /></div></div></CardContent>
          </Card>
        </div>

        <Card className="bg-white border border-[#E5DFD3] shadow-sm">
          <CardHeader><CardTitle className="font-heading text-xl text-[#1A1A1A] flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#8B1538]" /> Pagos Recientes</CardTitle></CardHeader>
          <CardContent>
            {recentPayments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E5DFD3]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Usuario</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Curso</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Monto</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Tipo</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Fecha</th>
                  </tr></thead>
                  <tbody>
                    {recentPayments.map((p) => (
                      <tr key={p.id} className="border-b border-[#E5DFD3] table-row-hover">
                        <td className="py-3 px-4"><div><p className="text-sm font-medium text-[#1A1A1A]">{p.user_name}</p><p className="text-xs text-[#737373]">{p.user_email}</p></div></td>
                        <td className="py-3 px-4 text-sm text-[#4A4A4A]">{p.course_title}</td>
                        <td className="py-3 px-4 text-sm font-medium text-[#8B1538]">${p.amount.toLocaleString()}</td>
                        <td className="py-3 px-4 text-sm text-[#4A4A4A] capitalize">{p.subscription_type}</td>
                        <td className="py-3 px-4 text-sm text-[#737373]">{new Date(p.created_at).toLocaleDateString('es-ES')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8"><CreditCard className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" /><p className="text-[#737373]">No hay pagos registrados</p></div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
